import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  Play,
  Pause,
  RotateCcw,
  RotateCw,
  Volume2,
  VolumeX,
  Maximize,
  Minimize,
  Subtitles,
  Settings,
  ArrowLeft,
  FastForward,
  Check,
  Tv,
  Cast,
  Moon,
  Sun,
  Sparkles,
  HelpCircle,
  X,
  SkipForward,
  Search,
} from 'lucide-react';
import Hls from 'hls.js';
import { api, apiUrl, withAccessToken, type MediaFile } from '../lib/api';
import { offlineStorage, type OfflineMediaItem } from '../lib/offlineStorage';
import { playbackLogger } from '../lib/playbackLogger';

export interface VideoPlayerProps {
  mediaItemId?: string;
  episodeId?: string;
  title: string;
  subtitle?: string; // e.g. "S1:E1 Pilot"
  file?: MediaFile;
  offlineItem?: OfflineMediaItem;
  localFileUrl?: string;
  initialTime?: number;
  introStart?: number;
  introEnd?: number;
  outroStart?: number;
  outroEnd?: number;
  onClose: () => void;
  nextEpisode?: {
    episodeId: string;
    title: string;
    stillPath?: string;
    file: MediaFile;
  };
  onPlayNext?: () => void;
}

export function VideoPlayer({
  mediaItemId,
  episodeId,
  title,
  subtitle,
  file,
  offlineItem,
  localFileUrl,
  initialTime = 0,
  introStart,
  introEnd,
  outroStart,
  outroEnd,
  onClose,
  nextEpisode,
  onPlayNext,
}: VideoPlayerProps) {
  const videoRef = useRef<HTMLVideoElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const hlsRef = useRef<Hls | null>(null);
  const controlsTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const heartbeatIntervalRef = useRef<NodeJS.Timeout | null>(null);

  // Playback state
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(initialTime);
  const [duration, setDuration] = useState(0);
  const [volume, setVolume] = useState(1);
  const [isMuted, setIsMuted] = useState(false);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [showControls, setShowControls] = useState(true);
  const [playbackSpeed, setPlaybackSpeed] = useState(1);

  // Movie Night Mode & Shortcuts
  const [movieNightMode, setMovieNightMode] = useState(false);
  const [showShortcuts, setShowShortcuts] = useState(false);
  const [canCast, setCanCast] = useState(false);

  // Quality & tracks
  const [streamType, setStreamType] = useState<'direct' | '1080p' | '720p' | '480p'>('direct');
  const [activeSubtitle, setActiveSubtitle] = useState<number | null>(null); // stream index or null
  const [onlineSubUrl, setOnlineSubUrl] = useState<string | null>(null);
  const [searchingSubs, setSearchingSubs] = useState(false);
  const [foundSubs, setFoundSubs] = useState<any[]>([]);
  const [showOnlineSubSearch, setShowOnlineSubSearch] = useState(false);
  const [subtitleSize, setSubtitleSize] = useState<'sm' | 'base' | 'lg'>('base');
  const [showSettingsMenu, setShowSettingsMenu] = useState(false);
  const [showSubtitlesMenu, setShowSubtitlesMenu] = useState(false);

  // Next Episode Countdown
  const [showNextPrompt, setShowNextPrompt] = useState(false);
  const [nextCountdown, setNextCountdown] = useState(10);
  const [nextCancelled, setNextCancelled] = useState(false);

  // Mobile double-tap & touch gesture indicators
  const [doubleTapFeedback, setDoubleTapFeedback] = useState<'left' | 'right' | null>(null);
  const [brightness, setBrightness] = useState(1);
  const [gestureFeedback, setGestureFeedback] = useState<{
    type: 'volume' | 'brightness' | 'seek';
    value: number | string;
  } | null>(null);
  const gestureFeedbackTimer = useRef<NodeJS.Timeout | null>(null);
  const touchStartPos = useRef<{ x: number; y: number; time: number } | null>(null);
  const touchActionType = useRef<'brightness' | 'volume' | 'seek' | null>(null);
  const initialTouchVal = useRef<number>(0);
  const lastTapRef = useRef<{ time: number; x: number } | null>(null);

  // Offline media playback state
  const [offlineVideoSrc, setOfflineVideoSrc] = useState<string | null>(null);
  const [offlineSubtitleTracks, setOfflineSubtitleTracks] = useState<
    Array<{ streamIndex: number; language: string; title: string; src: string }>
  >([]);

  useEffect(() => {
    const video = videoRef.current;
    if (video && 'remote' in video && (video as any).remote) {
      setCanCast(true);
    }
  }, []);

  const canSkipIntro =
    introStart !== undefined &&
    introEnd !== undefined &&
    currentTime >= introStart &&
    currentTime < introEnd;

  const canSkipOutro =
    outroStart !== undefined &&
    currentTime >= outroStart &&
    (outroEnd === undefined || currentTime < outroEnd);

  const handleSkipIntroOrOutro = useCallback(() => {
    const video = videoRef.current;
    if (!video) return;
    if (canSkipIntro && introEnd !== undefined) {
      video.currentTime = introEnd;
      setCurrentTime(introEnd);
    } else if (canSkipOutro) {
      if (nextEpisode && onPlayNext) {
        onPlayNext();
      } else if (outroEnd !== undefined) {
        video.currentTime = outroEnd;
        setCurrentTime(outroEnd);
      }
    }
  }, [canSkipIntro, canSkipOutro, introEnd, outroEnd, nextEpisode, onPlayNext]);

  const toggleSubtitlesQuick = () => {
    if (activeSubtitle !== null) {
      setActiveSubtitle(null);
    } else {
      const firstStream = file?.subtitleStreams?.[0]?.index ?? 0;
      setActiveSubtitle(firstStream);
    }
  };

  const handleCast = () => {
    const video = videoRef.current;
    if (video && (video as any).remote) {
      (video as any).remote.prompt().catch(() => {});
    }
  };

  useEffect(() => {
    if (!offlineItem) return;
    let isMounted = true;
    (async () => {
      try {
        const src = await offlineStorage.getVideoPlaybackSrc(offlineItem);
        if (!isMounted) return;
        setOfflineVideoSrc(src);

        const loadedSubs = await Promise.all(
          (offlineItem.subtitles || []).map(async (sub) => {
            const subSrc = await offlineStorage.getSubtitleSrc(sub);
            return {
              streamIndex: sub.streamIndex,
              language: sub.language,
              title: sub.title,
              src: subSrc,
            };
          })
        );
        if (!isMounted) return;
        setOfflineSubtitleTracks(loadedSubs);
        const defaultSub =
          loadedSubs.find((s) => s.language === 'heb' || s.language === 'he') || loadedSubs[0];
        if (defaultSub) {
          setActiveSubtitle(defaultSub.streamIndex);
        }
      } catch (err) {
        console.error('Failed to load offline media:', err);
      }
    })();
    return () => {
      isMounted = false;
    };
  }, [offlineItem]);

  // ─── Stream URL Builder ───
  const getStreamUrl = useCallback(
    (type: string) => {
      if (!file) return '';
      if (type === 'direct') {
        return apiUrl(`/api/v1/stream/${file.id}/direct`);
      }
      return apiUrl(`/api/v1/stream/${file.id}/hls/variant/${type}/index.m3u8`);
    },
    [file?.id],
  );

  // ─── Initialize Video Source (Offline / Direct Play / HLS) ───
  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;

    if (hlsRef.current) {
      hlsRef.current.destroy();
      hlsRef.current = null;
    }

    if (localFileUrl) {
      video.src = localFileUrl;
      video.currentTime = initialTime;
      video.play().catch(() => {});
      return;
    }

    if (offlineItem) {
      if (offlineVideoSrc) {
        video.src = offlineVideoSrc;
        video.currentTime = initialTime;
        video.play().catch(() => {});
      }
      return;
    }

    if (!file) return;

    const streamUrl = getStreamUrl(streamType);
    playbackLogger.info('VIDEO_PLAYER', 'Loading video stream source', {
      title,
      subtitle,
      streamType,
      mediaItemId,
      episodeId,
      initialTime,
      url: streamUrl,
    });

    if (streamType === 'direct') {
      video.src = withAccessToken(streamUrl);
      video.currentTime = initialTime;
      video.play().catch((err) => {
        playbackLogger.warn('VIDEO_PLAYER', 'Video play() autoplay prevented', { err: String(err) });
      });
    } else {
      if (Hls.isSupported()) {
        const hls = new Hls({
          enableWorker: true,
          lowLatencyMode: true,
          xhrSetup: (xhr) => {
            xhr.withCredentials = true;
            const token = localStorage.getItem('omflix_token');
            if (token) xhr.setRequestHeader('Authorization', `Bearer ${token}`);
          },
        });
        hls.loadSource(streamUrl);
        hls.attachMedia(video);
        hls.on(Hls.Events.MANIFEST_PARSED, () => {
          video.currentTime = initialTime;
          video.play().catch(() => {});
        });
        hlsRef.current = hls;
      } else if (video.canPlayType('application/vnd.apple.mpegurl')) {
        video.src = withAccessToken(streamUrl);
        video.currentTime = initialTime;
        video.play().catch(() => {});
      }
    }

    return () => {
      if (hlsRef.current) {
        hlsRef.current.destroy();
        hlsRef.current = null;
      }
    };
  }, [offlineItem, offlineVideoSrc, localFileUrl, streamType, file?.id, getStreamUrl, initialTime]);

  // ─── Watch Progress Heartbeat (every 5 seconds) ───
  useEffect(() => {
    if (!mediaItemId) return;
    heartbeatIntervalRef.current = setInterval(() => {
      const video = videoRef.current;
      if (video && !video.paused && video.duration > 0) {
        api.updateProgress({
          mediaItemId,
          episodeId,
          currentTime: video.currentTime,
          duration: video.duration,
        }).catch(() => {});
      }
    }, 5000);

    return () => {
      if (heartbeatIntervalRef.current) clearInterval(heartbeatIntervalRef.current);
      const video = videoRef.current;
      if (video && video.duration > 0) {
        api.updateProgress({
          mediaItemId,
          episodeId,
          currentTime: video.currentTime,
          duration: video.duration,
        }).catch(() => {});
      }
    };
  }, [mediaItemId, episodeId]);

  // ─── Controls Auto-Hide ───
  const resetControlsTimeout = useCallback(() => {
    setShowControls(true);
    if (controlsTimeoutRef.current) clearTimeout(controlsTimeoutRef.current);
    controlsTimeoutRef.current = setTimeout(() => {
      if (isPlaying) {
        setShowControls(false);
        setShowSettingsMenu(false);
        setShowSubtitlesMenu(false);
      }
    }, 3500);
  }, [isPlaying]);

  useEffect(() => {
    resetControlsTimeout();
    return () => {
      if (controlsTimeoutRef.current) clearTimeout(controlsTimeoutRef.current);
    };
  }, [resetControlsTimeout]);

  // ─── Keyboard Shortcuts ───
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.target instanceof HTMLInputElement || e.target instanceof HTMLTextAreaElement) return;

      switch (e.code) {
        case 'Space':
          e.preventDefault();
          togglePlay();
          break;
        case 'ArrowLeft':
          e.preventDefault();
          seekBy(-10);
          break;
        case 'ArrowRight':
          e.preventDefault();
          seekBy(10);
          break;
        case 'KeyJ':
          e.preventDefault();
          seekBy(-10);
          break;
        case 'KeyL':
          e.preventDefault();
          seekBy(10);
          break;
        case 'ArrowUp':
          e.preventDefault();
          adjustVolume(0.1);
          break;
        case 'ArrowDown':
          e.preventDefault();
          adjustVolume(-0.1);
          break;
        case 'KeyM':
          e.preventDefault();
          toggleMute();
          break;
        case 'KeyF':
          e.preventDefault();
          toggleFullscreen();
          break;
        case 'KeyS':
          e.preventDefault();
          handleSkipIntroOrOutro();
          break;
        case 'KeyN':
          if (nextEpisode && onPlayNext) {
            e.preventDefault();
            onPlayNext();
          }
          break;
        case 'KeyC':
          e.preventDefault();
          toggleSubtitlesQuick();
          break;
        case 'Slash':
          if (e.shiftKey) {
            e.preventDefault();
            setShowShortcuts((prev) => !prev);
          }
          break;
        case 'Escape':
          if (showShortcuts) {
            setShowShortcuts(false);
          } else if (!isFullscreen) {
            onClose();
          }
          break;
      }
      resetControlsTimeout();
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isFullscreen, onClose, resetControlsTimeout, handleSkipIntroOrOutro, nextEpisode, onPlayNext, showShortcuts]);

  // ─── Playback Actions ───
  const togglePlay = () => {
    const video = videoRef.current;
    if (!video) return;
    if (video.paused) {
      video.play();
      setIsPlaying(true);
    } else {
      video.pause();
      setIsPlaying(false);
    }
  };

  const seekBy = (seconds: number) => {
    const video = videoRef.current;
    if (!video) return;
    video.currentTime = Math.max(0, Math.min(video.duration, video.currentTime + seconds));
  };

  const handleSeek = (e: React.ChangeEvent<HTMLInputElement>) => {
    const video = videoRef.current;
    if (!video) return;
    const time = parseFloat(e.target.value);
    video.currentTime = time;
    setCurrentTime(time);
  };

  const adjustVolume = (delta: number) => {
    const video = videoRef.current;
    if (!video) return;
    const newVol = Math.max(0, Math.min(1, video.volume + delta));
    video.volume = newVol;
    setVolume(newVol);
    setIsMuted(newVol === 0);
  };

  const toggleMute = () => {
    const video = videoRef.current;
    if (!video) return;
    video.muted = !video.muted;
    setIsMuted(video.muted);
  };

  const toggleFullscreen = () => {
    if (!containerRef.current) return;
    if (!document.fullscreenElement) {
      containerRef.current.requestFullscreen().catch(() => {});
      setIsFullscreen(true);
    } else {
      document.exitFullscreen().catch(() => {});
      setIsFullscreen(false);
    }
  };

  const changeSpeed = (speed: number) => {
    const video = videoRef.current;
    if (!video) return;
    video.playbackRate = speed;
    setPlaybackSpeed(speed);
  };

  // ─── Time Update Handler & Next Episode Check ───
  const handleTimeUpdate = () => {
    const video = videoRef.current;
    if (!video) return;
    setCurrentTime(video.currentTime);
    setDuration(video.duration || 0);

    // Show the "Next Episode" prompt when > 92% watched
    // (only for episodes with a next episode available, and not if user cancelled)
    if (
      nextEpisode &&
      !nextCancelled &&
      video.duration > 60 &&
      video.currentTime / video.duration > 0.92
    ) {
      if (!showNextPrompt) {
        playbackLogger.info('VIDEO_PLAYER', 'Near end of episode – showing next episode prompt', {
          title,
          nextEpisode: nextEpisode?.title,
          progress: `${Math.round((video.currentTime / video.duration) * 100)}%`,
        });
        setShowNextPrompt(true);
      }
    }
  };

  // Next episode countdown tick
  useEffect(() => {
    if (!showNextPrompt || nextCancelled) return;
    const interval = setInterval(() => {
      setNextCountdown((prev) => {
        if (prev <= 1) {
          clearInterval(interval);
          playbackLogger.info('VIDEO_PLAYER', 'Next episode countdown completed, advancing', {
            title,
            nextEpisode: nextEpisode?.title,
          });
          onPlayNext?.();
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(interval);
  }, [showNextPrompt, nextCancelled, onPlayNext]);

  // Format seconds to HH:MM:SS
  const formatTime = (secs: number) => {
    if (isNaN(secs)) return '00:00';
    const h = Math.floor(secs / 3600);
    const m = Math.floor((secs % 3600) / 60);
    const s = Math.floor(secs % 60);
    if (h > 0) {
      return `${h}:${m.toString().padStart(2, '0')}:${s.toString().padStart(2, '0')}`;
    }
    return `${m.toString().padStart(2, '0')}:${s.toString().padStart(2, '0')}`;
  };

  // ─── Mobile Touch Gesture Handlers ───
  const handleTouchStart = (e: React.TouchEvent) => {
    if (e.touches.length !== 1) return;
    const touch = e.touches[0];
    if (!touch) return;
    const rect = containerRef.current?.getBoundingClientRect();
    if (!rect) return;

    const x = touch.clientX - rect.left;
    const y = touch.clientY - rect.top;
    const now = Date.now();

    // Check for double-tap seek
    if (lastTapRef.current && now - lastTapRef.current.time < 320) {
      const prevX = lastTapRef.current.x;
      const width = rect.width;
      if (Math.abs(x - prevX) < 120) {
        if (x < width * 0.4) {
          seekBy(-10);
          setDoubleTapFeedback('left');
          setTimeout(() => setDoubleTapFeedback(null), 800);
          lastTapRef.current = null;
          return;
        } else if (x > width * 0.6) {
          seekBy(10);
          setDoubleTapFeedback('right');
          setTimeout(() => setDoubleTapFeedback(null), 800);
          lastTapRef.current = null;
          return;
        }
      }
    }
    lastTapRef.current = { time: now, x };

    touchStartPos.current = { x, y, time: now };
    touchActionType.current = null;

    const width = rect.width;
    if (x < width * 0.45) {
      initialTouchVal.current = brightness;
    } else if (x > width * 0.55) {
      initialTouchVal.current = volume;
    } else {
      initialTouchVal.current = currentTime;
    }
  };

  const handleTouchMove = (e: React.TouchEvent) => {
    if (!touchStartPos.current || e.touches.length !== 1) return;
    const touch = e.touches[0];
    if (!touch) return;
    const rect = containerRef.current?.getBoundingClientRect();
    if (!rect) return;

    const currentX = touch.clientX - rect.left;
    const currentY = touch.clientY - rect.top;
    const deltaX = currentX - touchStartPos.current.x;
    const deltaY = touchStartPos.current.y - currentY; // positive when dragging upwards

    if (!touchActionType.current) {
      if (Math.abs(deltaX) > 25 && Math.abs(deltaX) > Math.abs(deltaY) * 1.3) {
        touchActionType.current = 'seek';
      } else if (Math.abs(deltaY) > 20) {
        if (touchStartPos.current.x < rect.width * 0.45) {
          touchActionType.current = 'brightness';
        } else if (touchStartPos.current.x > rect.width * 0.55) {
          touchActionType.current = 'volume';
        }
      }
    }

    if (touchActionType.current === 'brightness') {
      const step = deltaY / (rect.height * 0.5);
      const newBright = Math.max(0.2, Math.min(1.0, initialTouchVal.current + step));
      setBrightness(newBright);
      setGestureFeedback({ type: 'brightness', value: `${Math.round(newBright * 100)}%` });
    } else if (touchActionType.current === 'volume') {
      const step = deltaY / (rect.height * 0.5);
      const newVol = Math.max(0, Math.min(1.0, initialTouchVal.current + step));
      setVolume(newVol);
      if (videoRef.current) {
        videoRef.current.volume = newVol;
        videoRef.current.muted = newVol === 0;
      }
      setIsMuted(newVol === 0);
      setGestureFeedback({ type: 'volume', value: `${Math.round(newVol * 100)}%` });
    } else if (touchActionType.current === 'seek') {
      const seekSeconds = Math.round(deltaX / 8);
      const sign = seekSeconds > 0 ? '+' : '';
      setGestureFeedback({ type: 'seek', value: `${sign}${seekSeconds}s` });
    }
  };

  const handleTouchEnd = () => {
    if (touchActionType.current === 'seek' && gestureFeedback?.type === 'seek') {
      const valStr = String(gestureFeedback.value);
      const seconds = parseInt(valStr.replace('s', '').replace('+', ''), 10);
      if (!isNaN(seconds)) {
        seekBy(seconds);
      }
    }
    touchStartPos.current = null;
    touchActionType.current = null;

    if (gestureFeedbackTimer.current) clearTimeout(gestureFeedbackTimer.current);
    gestureFeedbackTimer.current = setTimeout(() => {
      setGestureFeedback(null);
    }, 600);
  };

  return (
    <div
      ref={containerRef}
      onMouseMove={resetControlsTimeout}
      onClick={resetControlsTimeout}
      onTouchStart={handleTouchStart}
      onTouchMove={handleTouchMove}
      onTouchEnd={handleTouchEnd}
      className={`fixed inset-0 z-50 bg-black flex items-center justify-center select-none overflow-hidden ${
        movieNightMode ? 'movie-night-active' : ''
      }`}
    >
      {/* ─── Cinema Movie Night Ambient Backglow ─── */}
      {movieNightMode && (
        <div className="absolute inset-0 pointer-events-none z-0 flex items-center justify-center overflow-hidden">
          <div className="w-[88%] h-[88%] rounded-[50px] bg-red-600/15 blur-[120px] animate-pulse" />
          <div className="absolute inset-0 bg-radial from-transparent via-black/40 to-black" />
        </div>
      )}

      {/* ─── Video Element ─── */}
      <video
        ref={videoRef}
        playsInline
        onPlay={() => {
          setIsPlaying(true);
          playbackLogger.info('VIDEO_PLAYER', 'Video playback started/resumed', { title, time: videoRef.current?.currentTime });
        }}
        onPause={() => {
          setIsPlaying(false);
          playbackLogger.info('VIDEO_PLAYER', 'Video playback paused', { title, time: videoRef.current?.currentTime });
        }}
        onEnded={() => {
          playbackLogger.info('VIDEO_PLAYER', 'Video reached end', {
            title,
            duration: videoRef.current?.duration,
            hasNext: Boolean(nextEpisode),
            countdownActive: showNextPrompt && !nextCancelled,
          });
          // IMPORTANT: Only auto-advance if the countdown prompt was NOT already shown.
          // If showNextPrompt is true, the countdown already called onPlayNext().
          // Calling it again here would cause a double-advance (switching to wrong episode).
          if (nextEpisode && onPlayNext && !showNextPrompt) {
            playbackLogger.info('VIDEO_PLAYER', 'Video ended – auto-advancing to next episode (no countdown was shown)', { title });
            onPlayNext();
          } else if (nextEpisode && showNextPrompt) {
            playbackLogger.info('VIDEO_PLAYER', 'Video ended – countdown already handled advance, skipping duplicate trigger', { title });
          }
        }}
        onError={() => {
          playbackLogger.error('VIDEO_PLAYER', 'Native video element error', {
            title,
            error: videoRef.current?.error?.message,
            code: videoRef.current?.error?.code,
          });
        }}
        onTimeUpdate={handleTimeUpdate}
        onLoadedMetadata={() => {
          if (videoRef.current) {
            setDuration(videoRef.current.duration);
            playbackLogger.info('VIDEO_PLAYER', 'Metadata loaded', {
              title,
              duration: videoRef.current.duration,
            });
          }
        }}
        onClick={togglePlay}
        className={`w-full h-full object-contain cursor-pointer relative z-10 transition-all duration-500 ${
          movieNightMode ? 'drop-shadow-[0_0_35px_rgba(229,9,20,0.3)]' : ''
        }`}
      >
        {/* Render subtitle track if selected */}
        {onlineSubUrl ? (
          <track
            key={onlineSubUrl}
            kind="subtitles"
            src={onlineSubUrl}
            srcLang="he"
            label="עברית (רשת)"
            default
          />
        ) : activeSubtitle !== null && (
          <track
            kind="subtitles"
            src={
              offlineItem
                ? offlineSubtitleTracks.find((s) => s.streamIndex === activeSubtitle)?.src || ''
                : withAccessToken(apiUrl(`/api/v1/stream/${file?.id}/subtitles/${activeSubtitle}.vtt`))
            }
            srcLang={
              offlineItem
                ? offlineSubtitleTracks.find((s) => s.streamIndex === activeSubtitle)?.language || 'en'
                : file?.subtitleStreams?.[activeSubtitle]?.language || 'en'
            }
            label={
              offlineItem
                ? offlineSubtitleTracks.find((s) => s.streamIndex === activeSubtitle)?.title || 'Subtitles'
                : file?.subtitleStreams?.[activeSubtitle]?.title || 'Subtitles'
            }
            default
          />
        )}
      </video>

      {/* ─── Mobile Double-Tap Seek Indicators ─── */}
      {doubleTapFeedback === 'left' && (
        <div className="absolute left-16 top-1/2 -translate-y-1/2 flex flex-col items-center justify-center bg-black/60 rounded-full w-24 h-24 pointer-events-none animate-pulse z-30">
          <RotateCcw className="w-10 h-10 text-white" />
          <span className="text-xs font-bold text-white mt-1">-10s</span>
        </div>
      )}
      {doubleTapFeedback === 'right' && (
        <div className="absolute right-16 top-1/2 -translate-y-1/2 flex flex-col items-center justify-center bg-black/60 rounded-full w-24 h-24 pointer-events-none animate-pulse z-30">
          <RotateCw className="w-10 h-10 text-white" />
          <span className="text-xs font-bold text-white mt-1">+10s</span>
        </div>
      )}

      {/* ─── Brightness Darkening Overlay ─── */}
      {brightness < 1 && (
        <div
          className="absolute inset-0 pointer-events-none z-20 bg-black transition-opacity"
          style={{ opacity: (1 - brightness) * 0.85 }}
        />
      )}

      {/* ─── Mobile Gesture HUD Pill ─── */}
      {gestureFeedback && (
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 z-40 pointer-events-none gesture-hud-pill px-6 py-3.5 flex items-center gap-3">
          {gestureFeedback.type === 'brightness' && <Sun className="w-6 h-6 text-amber-300 animate-pulse" />}
          {gestureFeedback.type === 'volume' && (
            volume === 0 || isMuted ? (
              <VolumeX className="w-6 h-6 text-red-400" />
            ) : (
              <Volume2 className="w-6 h-6 text-cyan-300 animate-pulse" />
            )
          )}
          {gestureFeedback.type === 'seek' && <FastForward className="w-6 h-6 text-white animate-pulse" />}
          <span className="text-lg font-black tracking-wide font-mono text-white">
            {gestureFeedback.value}
          </span>
        </div>
      )}

      {/* ─── Top Overlay Bar ─── */}
      <div
        className={`absolute top-0 left-0 right-0 p-4 sm:p-6 bg-gradient-to-b from-black/85 via-black/50 to-transparent flex items-center justify-between transition-opacity duration-300 video-player-top-bar z-30 ${
          showControls ? 'opacity-100' : 'opacity-0 pointer-events-none'
        }`}
      >
        <div className="flex items-center gap-4">
          <button
            onClick={onClose}
            className="p-2 rounded-full bg-white/10 hover:bg-white/20 text-white transition-colors cursor-pointer active:scale-95"
            title="Back to Homeflix"
          >
            <ArrowLeft className="w-6 h-6" />
          </button>
          <div>
            <h2 className="text-lg font-bold text-white drop-shadow">{title}</h2>
            {subtitle && <p className="text-sm text-neutral-400">{subtitle}</p>}
          </div>
        </div>

        {/* Action Controls & Badges */}
        <div className="flex items-center gap-2">
          {/* Movie Night Toggle */}
          <button
            onClick={() => setMovieNightMode((prev) => !prev)}
            className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-bold transition-all border cursor-pointer ${
              movieNightMode
                ? 'bg-amber-500/20 text-amber-300 border-amber-500/40 shadow-[0_0_15px_rgba(245,158,11,0.3)]'
                : 'bg-white/10 hover:bg-white/20 text-neutral-300 border-white/10'
            }`}
            title="Movie Night Mode (Ambient Cinema Atmosphere)"
          >
            <Moon className="w-4 h-4" />
            <span className="hidden sm:inline">Movie Night</span>
          </button>

          {/* Google Cast / Remote Play */}
          {canCast && (
            <button
              onClick={handleCast}
              className="p-2 rounded-lg bg-white/10 hover:bg-white/20 text-white transition-all cursor-pointer"
              title="Cast to Smart TV / Chromecast / AirPlay"
            >
              <Cast className="w-5 h-5 text-white" />
            </button>
          )}

          {/* Shortcuts Cheat Sheet Button */}
          <button
            onClick={() => setShowShortcuts(true)}
            className="p-2 rounded-lg bg-white/10 hover:bg-white/20 text-white transition-all cursor-pointer"
            title="Keyboard Shortcuts (?)"
          >
            <HelpCircle className="w-5 h-5 text-white/80" />
          </button>

          {/* Stream Badges */}
          {offlineItem ? (
            <span className="px-2.5 py-1 text-xs font-bold uppercase tracking-wider rounded bg-blue-500/20 text-blue-400 border border-blue-500/30 flex items-center gap-1.5">
              <span>נגן אופליין</span>
              <span>✈️</span>
            </span>
          ) : streamType === 'direct' ? (
            <span className="px-2.5 py-1 text-xs font-bold uppercase tracking-wider rounded bg-emerald-500/20 text-emerald-400 border border-emerald-500/30">
              Direct Play
            </span>
          ) : (
            <span className="px-2.5 py-1 text-xs font-bold uppercase tracking-wider rounded bg-amber-500/20 text-amber-400 border border-amber-500/30">
              NVENC Transcode ({streamType})
            </span>
          )}
        </div>
      </div>

      {/* ─── Skip Intro / Skip Outro Floating Button ─── */}
      {(canSkipIntro || canSkipOutro) && (
        <button
          onClick={handleSkipIntroOrOutro}
          className="absolute bottom-24 right-6 sm:right-10 z-40 flex items-center gap-2.5 px-6 py-3 rounded-xl bg-black/85 hover:bg-white text-white hover:text-black border-2 border-white/50 hover:border-white shadow-2xl backdrop-blur-xl font-bold text-sm sm:text-base tracking-wide transition-all duration-300 hover:scale-105 active:scale-95 cursor-pointer animate-in fade-in slide-in-from-bottom-3"
          title={canSkipIntro ? 'דלג על הפתיח (S)' : 'דלג על הקרדיטים (S)'}
        >
          <span>{canSkipIntro ? 'דילוג על הפתיח' : 'דילוג על הקרדיטים'}</span>
          <SkipForward className="w-5 h-5 fill-current" />
          <kbd className="text-[10px] px-1.5 py-0.5 rounded bg-white/20 text-neutral-300 font-mono font-bold">
            S
          </kbd>
        </button>
      )}

      {/* ─── Enhanced Next Episode Countdown Prompt ─── */}
      {showNextPrompt && nextEpisode && !nextCancelled && (
        <div className="absolute bottom-28 right-6 sm:right-8 z-40 bg-neutral-900/95 border border-white/20 rounded-2xl p-4 sm:p-5 shadow-2xl backdrop-blur-xl max-w-sm w-full animate-in fade-in slide-in-from-bottom-5">
          <div className="flex items-center justify-between mb-2">
            <span className="text-[11px] font-bold uppercase tracking-wider text-red-400 flex items-center gap-1.5">
              <Sparkles className="w-3.5 h-3.5" />
              הפרק הבא בעוד {nextCountdown} שניות
            </span>
            <span className="text-xs font-mono font-bold text-neutral-400">
              [קיצור: N]
            </span>
          </div>

          {nextEpisode.stillPath && (
            <div className="aspect-video w-full rounded-lg overflow-hidden mb-3 bg-black/40 border border-white/10">
              <img src={nextEpisode.stillPath} alt={nextEpisode.title} className="w-full h-full object-cover" />
            </div>
          )}

          <h4 className="text-sm sm:text-base font-bold text-white truncate mb-3">{nextEpisode.title}</h4>
          <div className="flex items-center gap-3">
            <button
              onClick={() => onPlayNext?.()}
              className="flex-1 py-2.5 px-4 rounded-xl bg-white hover:bg-neutral-200 text-black font-bold text-sm flex items-center justify-center gap-2 transition-all active:scale-95 shadow-lg cursor-pointer"
            >
              <FastForward className="w-4 h-4 fill-current" />
              נגן כעת
            </button>
            <button
              onClick={() => setNextCancelled(true)}
              className="py-2.5 px-4 rounded-xl bg-white/10 hover:bg-white/20 text-white font-medium text-sm transition-all active:scale-95 cursor-pointer"
            >
              בטל
            </button>
          </div>
        </div>
      )}

      {/* ─── Keyboard Shortcuts Cheat Sheet Modal ─── */}
      {showShortcuts && (
        <div className="absolute inset-0 z-50 bg-black/85 backdrop-blur-md flex items-center justify-center p-4 animate-in fade-in">
          <div className="bg-neutral-900/95 border border-white/20 rounded-2xl p-6 max-w-md w-full shadow-2xl relative" dir="rtl">
            <div className="flex items-center justify-between pb-4 mb-4 border-b border-white/10">
              <div className="flex items-center gap-2">
                <HelpCircle className="w-5 h-5 text-[#E50914]" />
                <h3 className="text-lg font-black text-white">מקשי קיצור בנגן</h3>
              </div>
              <button
                onClick={() => setShowShortcuts(false)}
                className="p-1.5 rounded-full hover:bg-white/10 text-neutral-400 hover:text-white transition-colors cursor-pointer"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="grid grid-cols-1 gap-2.5 text-sm max-h-[60vh] overflow-y-auto pr-1">
              {[
                { key: 'Space', desc: 'ניגון / השהיה' },
                { key: '← / →', desc: 'דילוג 10 שניות אחורה / קדימה' },
                { key: 'J / L', desc: 'דילוג 10 שניות (סגנון YouTube)' },
                { key: '↑ / ↓', desc: 'הגברה / הנמכת עוצמת שמע' },
                { key: 'M', desc: 'השתקה / ביטול השתקה' },
                { key: 'F', desc: 'מעבר למסך מלא' },
                { key: 'S', desc: 'דילוג על פתיח או קרדיטים' },
                { key: 'N', desc: 'מעבר מהיר לפרק הבא' },
                { key: 'C', desc: 'הפעלת / כיבוי כתוביות' },
                { key: '?', desc: 'פתיחת חלונית מקשים זו' },
                { key: 'Esc', desc: 'יציאה ממסך מלא או סגירה' },
              ].map((item, i) => (
                <div key={i} className="flex items-center justify-between p-2 rounded-xl bg-white/5 border border-white/5">
                  <span className="text-neutral-300 text-xs font-medium">{item.desc}</span>
                  <kbd className="px-2.5 py-1 rounded-lg bg-black/60 border border-white/20 text-xs font-mono font-bold text-white shadow-sm" dir="ltr">
                    {item.key}
                  </kbd>
                </div>
              ))}
            </div>
            <button
              onClick={() => setShowShortcuts(false)}
              className="mt-5 w-full py-2.5 rounded-xl bg-white/10 hover:bg-white/20 text-white font-bold text-xs transition-colors cursor-pointer"
            >
              הבנתי, חזור לנגן
            </button>
          </div>
        </div>
      )}

      {/* ─── Bottom Controls Overlay ─── */}
      {/* z-50 is required so fullscreen controls are always clickable above other layers */}
      <div
        className={`absolute bottom-0 left-0 right-0 p-6 bg-gradient-to-t from-black/90 via-black/50 to-transparent transition-opacity duration-300 z-50 ${
          showControls ? 'opacity-100' : 'opacity-0 pointer-events-none'
        }`}
      >
        {/* Timeline Scrubber */}
        <div className="relative group mb-4">
          <input
            type="range"
            min={0}
            max={duration || 100}
            step={0.1}
            value={currentTime}
            onChange={handleSeek}
            className="w-full h-1.5 bg-white/20 rounded-lg appearance-none cursor-pointer accent-[#E50914] focus:outline-none transition-all group-hover:h-2.5"
          />
          <div
            className="absolute left-0 top-0 h-1.5 bg-[#E50914] rounded-lg pointer-events-none group-hover:h-2.5 transition-all"
            style={{ width: `${(currentTime / (duration || 1)) * 100}%` }}
          />
        </div>

        {/* Controls Toolbar */}
        <div className="flex items-center justify-between">
          {/* Left: Play/Pause, Seek, Volume, Timers */}
          <div className="flex items-center gap-3 md:gap-4">
            <button
              onClick={togglePlay}
              className="p-2 text-white hover:text-[#E50914] transition-colors player-btn-hover cursor-pointer"
              title={isPlaying ? 'Pause (Space)' : 'Play (Space)'}
            >
              {isPlaying ? <Pause className="w-7 h-7" /> : <Play className="w-7 h-7 fill-current" />}
            </button>

            <button
              onClick={() => seekBy(-10)}
              className="p-2 text-white/80 hover:text-white transition-colors player-btn-hover cursor-pointer"
              title="Seek -10s (Left Arrow)"
            >
              <RotateCcw className="w-5 h-5" />
            </button>

            <button
              onClick={() => seekBy(10)}
              className="p-2 text-white/80 hover:text-white transition-colors player-btn-hover cursor-pointer"
              title="Seek +10s (Right Arrow)"
            >
              <RotateCw className="w-5 h-5" />
            </button>

            {/* Volume */}
            <div className="flex items-center gap-2 group">
              <button
                onClick={toggleMute}
                className="p-2 text-white/80 hover:text-white transition-colors player-btn-hover cursor-pointer"
                title="Mute (M)"
              >
                {isMuted || volume === 0 ? (
                  <VolumeX className="w-6 h-6 text-red-400" />
                ) : (
                  <Volume2 className="w-6 h-6" />
                )}
              </button>
              <input
                type="range"
                min={0}
                max={1}
                step={0.05}
                value={isMuted ? 0 : volume}
                onChange={(e) => {
                  const val = parseFloat(e.target.value);
                  setVolume(val);
                  if (videoRef.current) {
                    videoRef.current.volume = val;
                    videoRef.current.muted = val === 0;
                  }
                  setIsMuted(val === 0);
                }}
                className="w-0 group-hover:w-20 transition-all duration-200 h-1 bg-white/30 accent-white rounded cursor-pointer"
              />
            </div>

            {/* Time Indicator */}
            <span className="text-sm font-medium text-neutral-300 font-mono">
              {formatTime(currentTime)} / {formatTime(duration)}
            </span>
          </div>

          {/* Right: Subtitles, Settings, PIP, Fullscreen */}
          <div className="flex items-center gap-3 relative">
            {/* Subtitles Button & Popover */}
            <div className="relative">
              <button
                onClick={() => {
                  setShowSubtitlesMenu(!showSubtitlesMenu);
                  setShowSettingsMenu(false);
                }}
                className={`p-2 rounded-xl player-cc-hover cursor-pointer ${
                  activeSubtitle !== null
                    ? 'text-[#E50914] bg-white/15 ring-1 ring-[#E50914]/50'
                    : 'text-white/80 hover:text-white'
                }`}
                title="Subtitles (C)"
              >
                <Subtitles className="w-6 h-6" />
              </button>

              {showSubtitlesMenu && (
                <div className="absolute bottom-12 right-0 bg-neutral-950/95 border border-white/20 rounded-xl p-3 shadow-2xl backdrop-blur-lg w-64 z-40 animate-in fade-in">
                  <h4 className="text-xs font-bold uppercase tracking-wider text-neutral-400 mb-2 px-2">
                    Subtitles & Audio
                  </h4>

                  <div className="space-y-1 max-h-56 overflow-y-auto">
                    <button
                      onClick={() => {
                        setActiveSubtitle(null);
                        setShowSubtitlesMenu(false);
                      }}
                      className={`w-full text-left px-3 py-2 rounded-lg text-sm flex items-center justify-between ${
                        activeSubtitle === null
                          ? 'bg-white/20 text-white font-bold'
                          : 'text-neutral-300 hover:bg-white/10'
                      }`}
                    >
                      Off
                      {activeSubtitle === null && <Check className="w-4 h-4 text-emerald-400" />}
                    </button>

                    {offlineItem
                      ? offlineSubtitleTracks.map((sub) => (
                          <button
                            key={sub.streamIndex}
                            onClick={() => {
                              setActiveSubtitle(sub.streamIndex);
                              setShowSubtitlesMenu(false);
                            }}
                            className={`w-full text-left px-3 py-2 rounded-lg text-sm flex items-center justify-between cursor-pointer ${
                              activeSubtitle === sub.streamIndex
                                ? 'bg-white/20 text-white font-bold'
                                : 'text-neutral-300 hover:bg-white/10'
                            }`}
                          >
                            <span className="truncate">
                              {sub.language.toUpperCase()} - {sub.title}
                            </span>
                            {activeSubtitle === sub.streamIndex && (
                              <Check className="w-4 h-4 text-emerald-400" />
                            )}
                          </button>
                        ))
                      : ((file?.subtitleStreams || [])).map((sub) => (
                          <button
                            key={sub.index}
                            onClick={() => {
                              setActiveSubtitle(sub.index);
                              setShowSubtitlesMenu(false);
                            }}
                            className={`w-full text-left px-3 py-2 rounded-lg text-sm flex items-center justify-between cursor-pointer ${
                              activeSubtitle === sub.index
                                ? 'bg-white/20 text-white font-bold'
                                : 'text-neutral-300 hover:bg-white/10'
                            }`}
                          >
                            <span className="truncate">
                              {sub.language?.toUpperCase() || 'Track'} - {sub.title || sub.codec}
                            </span>
                            {activeSubtitle === sub.index && (
                              <Check className="w-4 h-4 text-emerald-400" />
                            )}
                          </button>
                        ))}
                  </div>

                  {/* Online Hebrew Subtitles Search Option */}
                  <div className="pt-2 mt-2 border-t border-white/10">
                    <button
                      onClick={async () => {
                        setShowOnlineSubSearch(!showOnlineSubSearch);
                        if (!showOnlineSubSearch && foundSubs.length === 0) {
                          setSearchingSubs(true);
                          try {
                            const res = await api.searchOnlineSubtitles({ query: title, lang: 'he' });
                            setFoundSubs(res);
                          } catch {
                            setFoundSubs([]);
                          } finally {
                            setSearchingSubs(false);
                          }
                        }
                      }}
                      className="w-full text-left px-2 py-1.5 rounded-lg text-xs font-bold text-amber-300 hover:bg-amber-500/10 flex items-center gap-2 cursor-pointer transition-all"
                    >
                      <Search className="w-3.5 h-3.5" />
                      <span>חפש כתוביות בעברית ברשת</span>
                    </button>

                    {showOnlineSubSearch && (
                      <div className="mt-2 space-y-1 max-h-36 overflow-y-auto">
                        {searchingSubs ? (
                          <p className="text-[11px] text-neutral-400 p-2 text-center animate-pulse">מחפש כתוביות...</p>
                        ) : foundSubs.length === 0 ? (
                          <p className="text-[11px] text-neutral-500 p-2 text-center">לא נמצאו כתוביות ברשת</p>
                        ) : (
                          foundSubs.slice(0, 5).map((sub, idx) => (
                            <button
                              key={sub.id || idx}
                              onClick={async () => {
                                try {
                                  const dl = await api.downloadSubtitle(sub.id, sub.url, 'he');
                                  if (dl?.url) {
                                    setOnlineSubUrl(withAccessToken(apiUrl(dl.url)));
                                    setActiveSubtitle(null);
                                    setShowSubtitlesMenu(false);
                                  }
                                } catch (e) {
                                  console.error(e);
                                }
                              }}
                              className="w-full text-left px-2 py-1.5 rounded text-[11px] text-neutral-300 hover:bg-white/15 truncate cursor-pointer block"
                            >
                              📥 {sub.releaseName || sub.title || `כתובית ${idx + 1}`}
                            </button>
                          ))
                        )}
                      </div>
                    )}
                  </div>

                  {/* Subtitle Size */}
                  <div className="mt-3 pt-2 border-t border-white/10 flex items-center justify-between px-2">
                    <span className="text-xs text-neutral-400">Size</span>
                    <div className="flex gap-1">
                      {(['sm', 'base', 'lg'] as const).map((s) => (
                        <button
                          key={s}
                          onClick={() => setSubtitleSize(s)}
                          className={`px-2 py-0.5 text-xs rounded ${
                            subtitleSize === s ? 'bg-white text-black font-bold' : 'text-neutral-400'
                          }`}
                        >
                          {s.toUpperCase()}
                        </button>
                      ))}
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* Quality & Speed Settings */}
            <div className="relative">
              <button
                onClick={() => {
                  setShowSettingsMenu(!showSettingsMenu);
                  setShowSubtitlesMenu(false);
                }}
                className={`p-2 rounded-xl text-white/80 hover:text-white transition-all duration-200 player-gear-hover cursor-pointer ${
                  showSettingsMenu ? 'bg-white/15 text-white ring-1 ring-white/30' : ''
                }`}
                title="Playback Settings"
              >
                <Settings className="w-6 h-6" />
              </button>

              {showSettingsMenu && (
                <div className="absolute bottom-12 right-0 bg-neutral-950/95 border border-white/20 rounded-xl p-3 shadow-2xl backdrop-blur-lg w-60 z-40 animate-in fade-in">
                  <h4 className="text-xs font-bold uppercase tracking-wider text-neutral-400 mb-2 px-2">
                    Quality
                  </h4>
                  <div className="space-y-1 mb-3">
                    {[
                      { key: 'direct', label: 'Original (Direct Play)' },
                      { key: '1080p', label: '1080p (Transcode)' },
                      { key: '720p', label: '720p (Transcode)' },
                      { key: '480p', label: '480p (Transcode)' },
                    ].map((q) => (
                      <button
                        key={q.key}
                        onClick={() => {
                          setStreamType(q.key as any);
                          setShowSettingsMenu(false);
                        }}
                        className={`w-full text-left px-3 py-1.5 rounded-lg text-sm flex items-center justify-between ${
                          streamType === q.key
                            ? 'bg-white/20 text-white font-bold'
                            : 'text-neutral-300 hover:bg-white/10'
                        }`}
                      >
                        {q.label}
                        {streamType === q.key && <Check className="w-4 h-4 text-emerald-400" />}
                      </button>
                    ))}
                  </div>

                  <h4 className="text-xs font-bold uppercase tracking-wider text-neutral-400 mb-2 px-2">
                    Speed
                  </h4>
                  <div className="grid grid-cols-4 gap-1 px-1">
                    {[0.75, 1, 1.25, 1.5].map((s) => (
                      <button
                        key={s}
                        onClick={() => changeSpeed(s)}
                        className={`py-1 rounded text-xs text-center font-medium ${
                          playbackSpeed === s
                            ? 'bg-white text-black font-bold'
                            : 'text-neutral-300 hover:bg-white/10'
                        }`}
                      >
                        {s}x
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </div>

            {/* Picture-in-Picture */}
            <button
              onClick={() => {
                if (document.pictureInPictureElement) {
                  document.exitPictureInPicture().catch(() => {});
                } else if (videoRef.current) {
                  videoRef.current.requestPictureInPicture().catch(() => {});
                }
              }}
              className="p-2 rounded-xl text-white/80 hover:text-white transition-all duration-200 player-btn-hover cursor-pointer"
              title="Picture-in-Picture"
            >
              <Tv className="w-5 h-5" />
            </button>

            {/* Fullscreen */}
            <button
              onClick={toggleFullscreen}
              className="p-2 rounded-xl text-white/80 hover:text-white transition-all duration-200 player-btn-hover cursor-pointer"
              title="Fullscreen (F)"
            >
              {isFullscreen ? <Minimize className="w-6 h-6" /> : <Maximize className="w-6 h-6" />}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
